import { getLocalStorageItem } from 'utils/browserCache';
import { getLastMessageFromAuthor } from 'utils/message';
import { LOCAL_STORAGE_KEY } from 'constants/browserCache';
import { FORM_TEMPLATE_TYPES } from 'constants/form';
import { CHATUI_WINDOW_PATH } from 'constants/global';
import { PARTICIPANT_NAME_SYSTEM_ACTUAL } from 'constants/twilio';
import { FormattedMessagesType } from 'routes/Chat/Messages/types';
import { AVATAR_ACTIVITY_STATE } from 'components/MessageExtensions/AvatarUpdate/AvatarUpdate.types';

export const getIsWaitingForAdvocateScenario = (msg: FormattedMessagesType | string): boolean => {
  if (!msg) return false;

  // Manually account for all system message intents that transfer to advocate. Hope to improve.
  const INTENT_FRAG1 = 'next customer advocate will be available'; // mapped intents: AI_CONTACT_ME_GCF_SELECT_CHAT_CARVANA, CONTACT_ME_GCF_SELECT_CHAT
  const INTENT_FRAG2 = 'ping an advocate to continue your conversation in chat'; // mapped intents: AI_CHAT_TO_SMS_CHOOSE_CHAT_CARVANA, AI_CHAT_TO_SMS_CHOOSE_CHAT_LONG_WT_CARVANA, CONTACT_ME_CHAT_TO_SMS_CUST_SELECTS_CHAT
  const INTENT_FRAG3 = 'better answer your question'; // mapped intents: ADVOCATE_AVAILABLE_CHAT_FOLLOW_UP_CARVANA, ADVOCATE_AVAILABLE_CONTACT_LATER_CARVANA
  const INTENT_FRAG4 = 'connect you with an advocate as soon as we can'; // mapped intents: AI_CONTACT_ME_GCF_SELECT_CHAT_LWT_CARVANA
  const INTENT_FRAG5 = 'confirming that I let an advocate know'; // mapped intents: CONTACT_ME_MOBILE_WEB_SUPPORT_FOLLOWUP_SUCCESS
  const INTENT_FRAG6 = 'What are we helping you with today?';
  const INTENT_FRAG7 = 'You should receive a confirmation text from us shortly';

  // Could be plain text or a message extension. Use JSON to handle both cases.
  const jsonMsg = JSON.stringify(msg);
  return (
    jsonMsg.includes(INTENT_FRAG1) ||
    jsonMsg.includes(INTENT_FRAG2) ||
    jsonMsg.includes(INTENT_FRAG3) ||
    jsonMsg.includes(INTENT_FRAG4) ||
    jsonMsg.includes(INTENT_FRAG5) ||
    jsonMsg.includes(INTENT_FRAG6) ||
    jsonMsg.includes(INTENT_FRAG7) ||
    jsonMsg.includes(AVATAR_ACTIVITY_STATE.IN_QUEUE) ||
    jsonMsg.includes(FORM_TEMPLATE_TYPES.RATING_REQUEST) || // can appear while waiting for advocate
    jsonMsg.includes(FORM_TEMPLATE_TYPES.GATED_FLOW_NAME) // escalation from Contact Flow
  );
};

export const getAndSetIsForcedSystemTypingAnimation = async (
  isCustomerMsg: boolean,
  isInitializing: boolean,
  cb: (val: boolean) => void,
  setLastForcedSysTypingAnimationTimestamp: (timestamp: number) => void,
) => {
  const isNoAdvocatesInConvo = window[CHATUI_WINDOW_PATH].getAdvocates()?.length === 0;
  const isCustomerToSystemMsg = isCustomerMsg && isNoAdvocatesInConvo;

  if (isCustomerToSystemMsg) {
    const chatUiCache = getLocalStorageItem(LOCAL_STORAGE_KEY.CVNA_CHAT_UI);

    // Message count from Twilio instance is 0 at time of initializing with question input/Popular Topic selection. To get
    // around this, reference cached message count from existing conversation with +1 for customer's new message.
    // If initialized via "Continue Conversation", instance will reflect correct message count.
    const msgCountInit = isInitializing
      ? chatUiCache?.conversationMsgCount + 1 || 0
      : (await window[CHATUI_WINDOW_PATH].getMessages()).length;
    const ANIMATION_DELAY = 3000;
    setTimeout(async () => {
      const messagesDelay = await window[CHATUI_WINDOW_PATH].getMessages();
      const msgCountDelay = messagesDelay.length;

      // Iterate from end of conversation to find the latest system message. This will then be used to
      // identify if this incoming message qualifies as special case for forced animation.
      const lastMsgAdvocate = await getLastMessageFromAuthor(PARTICIPANT_NAME_SYSTEM_ACTUAL);

      // Handle special case where Sebastian stops responding once customer is transferred to assignment queue.
      const isWaitingForAdvocateScenario = Boolean(
        lastMsgAdvocate && getIsWaitingForAdvocateScenario(lastMsgAdvocate),
      );
      if (isWaitingForAdvocateScenario) {
        cb(false);
        return;
      }

      const isConversationFirstMsg = msgCountInit === 0 && msgCountDelay === 1;
      if (isConversationFirstMsg || msgCountInit === msgCountDelay) {
        cb(true);
        setLastForcedSysTypingAnimationTimestamp(Date.now() - ANIMATION_DELAY);
      }
    }, ANIMATION_DELAY);
  } else {
    // Don't add condition check. After intro of forced delay from GPT, always set state value. Timing & flow of incoming messages vary.
    cb(false);
  }
};
